import {
    InspectionWorkflow,
    WorkflowStep,
    IterateStep,
} from "../types/inspectionWorkflow";
import { InspectionResult } from "src/services/inspectionService";
import { getQuestionIdsFromStep } from "./checkpointExtractor";

const WASTE_LABEL_TARGETS = ["waste label", "printed waste label"];

const isWasteLabelStep = (step: WorkflowStep): boolean =>
    "type" in step &&
    step.type === "detect_object" &&
    step.target.some((t: string) => WASTE_LABEL_TARGETS.includes(t.toLowerCase()));

/**
 * The position at which a resumed draft should continue.
 *
 * When `complete` is true the draft has every checkpoint captured; the caller
 * should route to the review page instead of the executor, and the
 * category/step indices are not meaningful.
 */
export interface ResumePosition {
    /** Category index of the step to resume at within the workflow. */
    categoryIndex: number;
    /** Step index within the category to resume at. */
    stepIndex: number;
    /** For a mid cage-walk resume: the sub-step index inside the iterate step. */
    iterateStepIndex?: number;
    /** Number of fully-captured containers so far (the header's container number). */
    completedContainerCount?: number;
    /** Barcode to pre-load for an in-progress container. */
    scannedBarcode?: string;
    /** objectId of the last fully-captured container. */
    lastCompletedBarcode?: string;
    /** True when every checkpoint is captured — route to review, not the executor. */
    complete?: boolean;
}

/**
 * Returns true when the resume position targets the cage-walk category
 * (the category containing an iterate step).
 */
export const isCageWalkResume = (
    workflow: InspectionWorkflow,
    pos: ResumePosition
): boolean => {
    if (pos.complete) return false;
    const category = workflow.categories[pos.categoryIndex];
    return category?.steps.some((s) => "iterate" in s) ?? false;
};

/**
 * Computes where a resumed draft should continue, from the workflow shape and
 * the draft's saved results.
 *
 * "Captured" for a question id = it appears on at least one result row (any
 * status, including PENDING). Results referencing question ids that are not in
 * the workflow are ignored (they never match a step's question ids).
 *
 * Pure and side-effect free.
 */
export const computeResumePosition = (
    workflow: InspectionWorkflow,
    results: InspectionResult[]
): ResumePosition => {
    const answeredQuestionIds = new Set(results.map((r) => r.questionId));
    const hasResult = (qid: string): boolean => answeredQuestionIds.has(qid);

    const cageWalkIndex = workflow.categories.findIndex((c) =>
        c.steps.some((s) => "iterate" in s)
    );

    // A category is "started" once any of its question ids has a result.
    const categoryStarted = (category: InspectionWorkflow["categories"][number]): boolean =>
        category.steps.some(
            (step) => !("iterate" in step) && getQuestionIdsFromStep(step).some(hasResult)
        );

    for (let ci = 0; ci < workflow.categories.length; ci++) {
        const category = workflow.categories[ci];

        if (ci === cageWalkIndex) {
            // The cage walk is open-ended (no fixed container count), so it has
            // no "all captured" completion signal. Instead, infer completion
            // from forward progress: if any later category has been started, the
            // inspector already moved past the cage walk — skip it.
            const movedPastCageWalk = workflow.categories
                .slice(ci + 1)
                .some(categoryStarted);
            if (movedPastCageWalk) continue;

            // Resume within (or at the start of) the cage walk.
            return cageWalkPosition(ci, category.steps, results);
        }

        // Non-cage-walk category: resume at the first step whose question ids are
        // not all answered.
        const stepIndex = category.steps.findIndex((step) => {
            if ("iterate" in step) return false; // no iterate steps outside the cage walk
            return !getQuestionIdsFromStep(step).every(hasResult);
        });
        if (stepIndex !== -1) {
            return { categoryIndex: ci, stepIndex };
        }
        // Fully answered category — continue to the next one.
    }

    // Everything answered -> route to review. Category/step indices are unused.
    return { categoryIndex: 0, stepIndex: 0, complete: true };
};

/**
 * Resume position within the cage-walk category. Groups cage-walk results by
 * container (objectId); a container is complete once it has a waste-label
 * result. Resumes the in-progress container at the waste-label step, or
 * starts a fresh barcode scan when every container is complete.
 */
const cageWalkPosition = (
    categoryIndex: number,
    steps: (WorkflowStep | IterateStep)[],
    results: InspectionResult[]
): ResumePosition => {
    const iterateStepIndexInCategory = steps.findIndex((s) => "iterate" in s);
    const iterateSteps = (steps[iterateStepIndexInCategory] as IterateStep).iterate;

    const cageWalkStepLocation: Pick<ResumePosition, "categoryIndex" | "stepIndex"> = {
        categoryIndex,
        stepIndex: iterateStepIndexInCategory,
    };

    // The waste-label capture step (identified by target) marks a container as
    // complete. Its sub-step index is where an in-progress container resumes.
    const wasteLabelSubStepIndex = iterateSteps.findIndex(isWasteLabelStep);
    const wasteLabelQuestionIds = new Set(
        wasteLabelSubStepIndex === -1
            ? []
            : getQuestionIdsFromStep(iterateSteps[wasteLabelSubStepIndex])
    );

    // All question ids across the iterate step's capture sub-steps, used to
    // pick out cage-walk results and group them into containers.
    const cageWalkQuestionIds = new Set(
        iterateSteps.flatMap(getQuestionIdsFromStep)
    );

    // Group cage-walk results by container (objectId): each container maps to the
    // set of cage-walk question ids answered for it. Only results with a non-null
    // objectId and a known cage-walk question id count. A Map preserves insertion
    // order, so its keys give containers in first-appearance order.
    const answeredByContainer = results
        .filter((r) => !!r.objectId && cageWalkQuestionIds.has(r.questionId))
        .reduce((map, r) => {
            const answered = map.get(r.objectId!) ?? new Set<string>();
            answered.add(r.questionId);
            return map.set(r.objectId!, answered);
        }, new Map<string, Set<string>>());

    const containersInOrder = [...answeredByContainer.keys()];

    // A container is complete once it has a result for any waste-label question.
    const isComplete = (containerId: string): boolean => {
        const answered = answeredByContainer.get(containerId)!;
        return [...answered].some((qid) => wasteLabelQuestionIds.has(qid));
    };

    const completedContainerCount = containersInOrder.filter(isComplete).length;
    // Containers are captured sequentially, so at most one can be in progress
    // (has results but no waste-label result).
    const inProgressContainer = containersInOrder.find((id) => !isComplete(id));

    // Resume the in-progress container at the waste-label sub-step, with its
    // barcode pre-loaded.
    if (inProgressContainer && wasteLabelSubStepIndex !== -1) {
        return {
            ...cageWalkStepLocation,
            iterateStepIndex: wasteLabelSubStepIndex,
            scannedBarcode: inProgressContainer,
            completedContainerCount,
        };
    }

    // All containers complete (or none yet) -> start the next container at the
    // barcode scan (sub-step 0).
    return {
        ...cageWalkStepLocation,
        iterateStepIndex: 0,
        completedContainerCount,
        lastCompletedBarcode: containersInOrder[containersInOrder.length - 1],
    };
};
